import {Locations} from 'utils/constants';

import type {PostMenuItems, UsePostMenuItemsParams} from './types';

const rhsLocations = new Set<string>([
    Locations.RHS_ROOT,
    Locations.RHS_COMMENT,
    Locations.SEARCH,
    Locations.SAVED,
    Locations.PINNED,
]);
export function isRHSLocation(location: UsePostMenuItemsParams['location']) {
    return rhsLocations.has(location);
}

const threadLocations = new Set<string>([Locations.RHS_ROOT, Locations.RHS_COMMENT]);
export function isThreadLocation(location: UsePostMenuItemsParams['location']) {
    return threadLocations.has(location);
}

const centerOrThreadLocations = new Set<string>([Locations.CENTER, Locations.RHS_ROOT, Locations.RHS_COMMENT]);
export function isCenterOrThreadLocation(location: UsePostMenuItemsParams['location']) {
    return centerOrThreadLocations.has(location);
}

export function createEmptyPostMenuItems(): PostMenuItems {
    return {
        ADD_EMOJI: null,
        REPLY: null,
        DISCUSS_MESSAGE: null,
        POST_FOLLOW: null,
        MARK_AS_UNREAD: null,
        REMOVE_FROM_SAVED: null,
        SAVE: null,
        PLUGINS: null,
        PIN: null,
        UNPIN: null,
        REACTIONS: null,
        COPY_LINK: null,
        COPY_TEXT: null,
        EDIT: null,
        DELETE: null,
        JUMP: null,
    };
}

function isTextNode(node: Node) {
    return node.nodeType === Node.TEXT_NODE;
}

function isPointInsideRect(rect: DOMRect, x: number, y: number) {
    return (
        Math.floor(rect.top) <= y &&
        Math.ceil(rect.right) >= x &&
        Math.ceil(rect.bottom) >= y &&
        Math.floor(rect.left) <= x
    );
}

/**
 * Возвращает символ в текстовой ноде по переданным координатам.
 */
function getCharacterAtPoint(node: Node, x: number, y: number) {
    const range = node.ownerDocument?.createRange();

    if (!range) {
        return '';
    }

    range.selectNodeContents(node);

    let result = '';
    let start = 0;
    const end = range.endOffset;

    while (start < end) {
        range.setStart(node, start);
        range.setEnd(node, start + 1);

        if (isPointInsideRect(range.getBoundingClientRect(), x, y)) {
            result = range.toString();
            break;
        }

        start += 1;
    }

    range.detach();
    return result;
}

/**
 * Проверят наличие текста по переданным координатам.
 */
function isTextAtXY(x: number, y: number) {
    const rangeOrCaretPos = document.caretPositionFromPoint?.(x, y) || document.caretRangeFromPoint?.(x, y);

    if (!rangeOrCaretPos) {
        return false;
    }

    const node = 'offsetNode' in rangeOrCaretPos ? rangeOrCaretPos.offsetNode : rangeOrCaretPos.startContainer;

    if (!isTextNode(node)) {
        return false;
    }

    return getCharacterAtPoint(node, x, y).length > 0;
}

const ignoredTags = new Set<string>(['A', 'IMG', 'TIME', 'TEXTAREA', 'INPUT']);

export function shouldShowPostMenu(event: React.MouseEvent) {
    if (event.isDefaultPrevented()) {
        return false;
    }

    const target = event.target as HTMLElement;

    if (ignoredTags.has(target.tagName)) {
        return false;
    }

    if (target.closest('a')) {
        return false;
    }

    return !isTextAtXY(event.pageX, event.pageY);
}
